import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Activity, BellRing, Blocks, CalendarClock, Plus, Search, TriangleAlert, UserRound, Zap } from "lucide-react";
import { usePermission } from "@ncp/api-client";
import { formatRelative, useLocale, useTranslation, type Locale } from "@ncp/i18n";
import { slugFromLabel } from "@ncp/forms";
import {
  Badge, Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, EmptyState, Field, Input, NativeSelect, PageHeader, Skeleton, StatCard,
  Tabs, TabsContent, TabsList, TabsTrigger, Tooltip, cn, notifySaved,
} from "@ncp/ui";
import { useBreadcrumbs, useHelpTopic, usePaletteActions } from "../app/console-context";
import { SavedViewsMenu } from "../components/SavedViews";
import { emptyAutomation, useAutomationActions, useAutomationOptions, useAutomationRuns, useAutomations, type Automation, type AutomationScope } from "../features/automations/api";
import { RUN_VARIANT, RunsTable } from "../features/automations/RunsTable";

interface ViewState { q: string; scope: string; state: string; workspace: string }
const DEFAULT_VIEW: ViewState = { q: "", scope: "", state: "", workspace: "" };
const SCOPE_ICON = { Platform: Zap, Workspace: Blocks, Personal: UserRound } as const;

/** Every automation on the platform: console-authored platform rules, workspace automations and personal watchers, with their run log. */
export function AutomationsPage() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "runs" ? "runs" : "list";
  const { can } = usePermission();
  const canWrite = can("platform.automations.write");
  const [creating, setCreating] = useState(false);
  const [view, setView] = useState<ViewState>(DEFAULT_VIEW);
  const list = useAutomations(view.scope || undefined, view.q || undefined);
  const runs = useAutomationRuns(undefined, undefined, 200);

  useBreadcrumbs(useMemo(() => [{ label: t("admin:nav.automations") }], [t]));
  useHelpTopic({ title: t("admin:automations.title"), docsAnchor: "automations", body: (<><p>{t("admin:automations.help.what")}</p><p>{t("admin:automations.help.scopes")}</p><p>{t("admin:automations.help.safety")}</p></>) });
  usePaletteActions(useMemo(() => canWrite ? [{ id: "automations.new", label: t("admin:automations.new"), icon: Plus, permission: "platform.automations.write", run: () => setCreating(true) }] : [], [canWrite, t]));

  const all = list.data ?? [];
  const workspaces = useMemo(() => [...new Set(all.map((a) => a.workspaceKey).filter((k): k is string => !!k))].sort(), [all]);
  const sharedIn = new Set(all.filter((a) => a.scope === "Workspace" && a.workspaceKey).map((a) => a.workspaceKey)).size;
  const rows = useMemo(() => all.filter((a) =>
    (!view.workspace || a.workspaceKey === view.workspace)
    && (!view.state || (view.state === "on" ? a.enabled && a.status === "Published" : view.state === "paused" ? !a.enabled && a.status === "Published" : view.state === "draft" ? a.status !== "Published" || a.draftVersion != null : view.state === "failing" ? a.lastStatus === "Failed" : true))), [all, view]);
  const recent = runs.data ?? [];
  const weekAgo = Date.now() - 7 * 86_400_000;
  const week = recent.filter((r) => r.status !== "DryRun" && new Date(r.createdAt).getTime() >= weekAgo);
  const count = (s: AutomationScope) => all.filter((a) => a.scope === s).length;
  const setTab = (v: string) => setParams((p) => { if (v === "runs") p.set("tab", "runs"); else p.delete("tab"); return p; }, { replace: true });

  return (
    <div>
      <PageHeader title={t("admin:automations.title")} description={t("admin:automations.subtitle")} actions={canWrite ? <Button variant="gold" onClick={() => setCreating(true)}><Plus /> {t("admin:automations.new")}</Button> : undefined} />

      <div className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label={t("admin:automations.stats.platform")} value={count("Platform")} icon={Zap} tone="gold" footer={t("admin:automations.stats.platformHint")} />
        <StatCard label={t("admin:automations.stats.workspace")} value={count("Workspace")} icon={Blocks} tone="sea" footer={t("admin:automations.stats.workspaceHint", { count: sharedIn })} />
        <StatCard label={t("admin:automations.stats.personal")} value={count("Personal")} icon={UserRound} tone="camel" footer={t("admin:automations.stats.personalHint")} />
        <StatCard label={t("admin:automations.stats.runsWeek")} value={week.length} icon={Activity} tone="neutral" footer={t("admin:automations.stats.failedWeek", { count: week.filter((r) => r.status === "Failed").length })} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-3">
          <TabsTrigger value="list"><Zap className="size-3.5" /> {t("admin:automations.tabs.list")} <Badge variant="outline" size="sm" className="ms-1">{all.length}</Badge></TabsTrigger>
          <TabsTrigger value="runs"><Activity className="size-3.5" /> {t("admin:automations.tabs.runs")}</TabsTrigger>
        </TabsList>

        <TabsContent value="list">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <div className="relative"><Search className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" /><Input value={view.q} onChange={(e) => setView({ ...view, q: e.target.value })} placeholder={t("admin:automations.searchPlaceholder")} className="h-9 w-64 ps-8" /></div>
            <NativeSelect value={view.scope} className="h-9 w-48" onChange={(e) => setView({ ...view, scope: e.target.value })}>
              <option value="">{t("admin:automations.columns.scope")}: {t("admin:common.all")}</option>
              {(["Platform", "Workspace", "Personal"] as const).map((s) => <option key={s} value={s}>{t(`admin:automations.scope.${s}`)}</option>)}
            </NativeSelect>
            <NativeSelect value={view.state} className="h-9 w-44" onChange={(e) => setView({ ...view, state: e.target.value })}>
              <option value="">{t("admin:common.status")}: {t("admin:common.all")}</option>
              {(["on", "paused", "draft", "failing"] as const).map((s) => <option key={s} value={s}>{t(`admin:automations.filters.${s}`)}</option>)}
            </NativeSelect>
            {workspaces.length ? <NativeSelect value={view.workspace} className="h-9 w-48" onChange={(e) => setView({ ...view, workspace: e.target.value })}><option value="">{t("admin:nav.workspaces")}: {t("admin:common.all")}</option>{workspaces.map((w) => <option key={w} value={w}>{w}</option>)}</NativeSelect> : null}
            <div className="ms-auto"><SavedViewsMenu tableKey="automations" current={view} onApply={setView} /></div>
          </div>

          {list.isLoading ? <div className="space-y-2"><Skeleton className="h-14" /><Skeleton className="h-14" /><Skeleton className="h-14" /></div> : rows.length === 0 ? (
            <EmptyState icon={Zap} title={all.length === 0 ? t("admin:automations.emptyTitle") : t("admin:automations.noMatch")} description={all.length === 0 ? t("admin:automations.emptyBody") : undefined}
              action={canWrite && all.length === 0 ? <Button variant="gold" onClick={() => setCreating(true)}><Plus /> {t("admin:automations.new")}</Button> : undefined} />
          ) : (
            <div className="overflow-hidden rounded-2xl border border-sand-200 bg-white/80">
              <table className="w-full text-sm">
                <thead className="bg-sand-50 text-xs text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2 text-start font-medium">{t("admin:common.name")}</th>
                    <th className="px-3 py-2 text-start font-medium">{t("admin:automations.columns.when")}</th>
                    <th className="px-3 py-2 text-start font-medium">{t("admin:automations.columns.then")}</th>
                    <th className="px-3 py-2 text-start font-medium">{t("admin:forms.columns.published")}</th>
                    <th className="px-3 py-2 text-start font-medium">{t("admin:automations.columns.lastRun")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-sand-100">
                  {rows.map((a) => <Row key={a.id} a={a} locale={locale} onOpen={() => navigate(`/automations/${encodeURIComponent(a.key)}`)} />)}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="runs"><RunsTable runs={recent} loading={runs.isLoading} /></TabsContent>
      </Tabs>

      <CreateAutomationDialog open={creating} onOpenChange={setCreating} existing={all} onCreated={(key) => navigate(`/automations/${encodeURIComponent(key)}`)} />
    </div>
  );
}

function Row({ a, locale, onOpen }: { a: Automation; locale: Locale; onOpen: () => void }) {
  const { t } = useTranslation();
  const Icon = a.trigger === "schedule.cron" ? CalendarClock : a.scope === "Personal" ? BellRing : SCOPE_ICON[a.scope];
  const name = locale === "ar" && a.nameAr ? a.nameAr : a.name;
  const then = locale === "ar" ? a.thenAr : a.then;
  const live = a.status === "Published";
  return (
    <tr className={cn("cursor-pointer transition-colors hover:bg-aegold-50/40", live && !a.enabled && "opacity-60")} onClick={onOpen} onKeyDown={(e) => { if (e.key === "Enter") onOpen(); }} tabIndex={0}>
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-2.5">
          <span className={cn("grid size-8 shrink-0 place-items-center rounded-xl", a.scope === "Platform" ? "bg-aegold-50 text-aegold-700" : a.scope === "Workspace" ? "bg-seablue-50 text-seablue-700" : "bg-camel-50 text-camel-700")}><Icon className="size-4" /></span>
          <div className="min-w-0">
            <div className="truncate font-medium">{name}</div>
            <div className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
              <Badge variant="outline" size="sm">{t(`admin:automations.scope.${a.scope}`)}</Badge>
              {a.workspaceKey ? <Badge variant="outline" size="sm" className="font-mono">{a.workspaceKey}</Badge> : null}
              {a.scope === "Personal" && a.ownerName ? <span>{a.ownerName}</span> : null}
            </div>
          </div>
        </div>
      </td>
      <td className="max-w-[260px] px-3 py-2.5 text-xs"><span className="line-clamp-2">{locale === "ar" ? a.whenAr : a.when}</span></td>
      <td className="max-w-[280px] px-3 py-2.5 text-xs"><span className="line-clamp-2">{then.join(locale === "ar" ? "، " : ", ") || "—"}</span></td>
      <td className="px-3 py-2.5">
        <div className="flex flex-wrap items-center gap-1">
          {live ? <Badge variant={a.enabled ? "success" : "default"} size="sm" dot>v{a.version} · {a.enabled ? t("admin:automations.state.on") : t("admin:automations.state.paused")}</Badge>
            : <Badge variant={a.status === "InReview" ? "warning" : "info"} size="sm" dot>v{a.version} · {t(`admin:forms.status.${a.status}`)}</Badge>}
          {a.draftVersion != null ? <Badge variant={a.draftStatus === "InReview" ? "warning" : "info"} size="sm">v{a.draftVersion} · {t(`admin:forms.status.${a.draftStatus ?? "Draft"}`)}</Badge> : null}
        </div>
        {a.pausedReason ? <Tooltip content={a.pausedReason}><span className="mt-0.5 block max-w-[200px] truncate text-[11px] text-camel-800">{a.pausedReason}</span></Tooltip> : null}
      </td>
      <td className="px-3 py-2.5 text-xs">
        {a.lastRunAt ? (
          <div className="flex flex-col gap-0.5">
            <span className="text-muted-foreground">{formatRelative(a.lastRunAt, locale)}</span>
            <span className="flex items-center gap-1">
              {a.lastStatus ? <Badge variant={RUN_VARIANT[a.lastStatus] ?? "default"} size="sm">{t(`admin:automations.runStatus.${a.lastStatus}`, { defaultValue: a.lastStatus })}</Badge> : null}
              <span className="tabular text-muted-foreground">{t("admin:automations.runCount", { runs: a.runCount })}</span>
              {a.failureCount > 0 ? <span className="flex items-center gap-0.5 text-danger-700"><TriangleAlert className="size-3" /> {a.failureCount}</span> : null}
            </span>
          </div>
        ) : <span className="text-muted-foreground">{t("admin:automations.neverRan")}</span>}
      </td>
    </tr>
  );
}

function CreateAutomationDialog({ open, onOpenChange, existing, onCreated }: { open: boolean; onOpenChange: (o: boolean) => void; existing: Automation[]; onCreated: (key: string) => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const actions = useAutomationActions();
  const options = useAutomationOptions();
  const [name, setName] = useState("");
  const [nameAr, setNameAr] = useState("");
  const [key, setKey] = useState("");
  const [keyTouched, setKeyTouched] = useState(false);
  const [scope, setScope] = useState<AutomationScope>("Platform");
  const [workspace, setWorkspace] = useState("");
  const [start, setStart] = useState<"event" | "schedule">("event");
  const effectiveKey = keyTouched ? key : slugFromLabel(name);
  const keyError = !effectiveKey ? null : !/^[a-z][a-z0-9._-]{1,63}$/.test(effectiveKey) ? t("admin:agents.create.keyInvalid") : existing.some((a) => a.key === effectiveKey) ? t("admin:agents.create.keyTaken") : null;
  const ready = name.trim().length > 1 && !!effectiveKey && !keyError && (scope === "Platform" || !!workspace);
  const reset = () => { setName(""); setNameAr(""); setKey(""); setKeyTouched(false); setScope("Platform"); setWorkspace(""); setStart("event"); };

  const create = async () => {
    try {
      const a = await actions.create.mutateAsync({ key: effectiveKey, body: emptyAutomation(name.trim(), nameAr.trim() || null, scope, workspace || null, start === "schedule") });
      notifySaved(a, t("admin:automations.create.created"));
      onOpenChange(false); reset();
      onCreated(a.key);
    } catch { /* useSave reported the failure with its cause; the dialog stays open */ }
  };

  const choice = (active: boolean) => cn("rounded-xl border p-3 text-start transition-colors", active ? "border-aegold-400 bg-aegold-50/60" : "border-sand-200 hover:bg-sand-50");
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent size="md">
        <DialogHeader><DialogTitle>{t("admin:automations.new")}</DialogTitle><DialogDescription>{t("admin:automations.create.intro")}</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          {(["Platform", "Workspace"] as const).map((s) => {
            const Icon = SCOPE_ICON[s];
            return (
              <button key={s} type="button" onClick={() => setScope(s)} className={choice(scope === s)}>
                <Icon className="mb-1 size-4 text-aegold-700" />
                <div className="text-sm font-medium">{t(`admin:automations.scope.${s}`)}</div>
                <div className="text-xs text-muted-foreground">{t(`admin:automations.create.scopeHint.${s}`)}</div>
              </button>
            );
          })}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {scope === "Workspace" ? (
            <Field label={t("admin:automations.fields.workspace")} className="sm:col-span-2" required>
              <NativeSelect value={workspace} onChange={(e) => setWorkspace(e.target.value)}>
                <option value="">—</option>
                {(options.data?.workspaces ?? []).map((w) => <option key={w.key} value={w.key}>{locale === "ar" && w.nameAr ? w.nameAr : w.name}</option>)}
              </NativeSelect>
            </Field>
          ) : null}
          <Field label={t("admin:common.name")} required><Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("admin:automations.create.namePlaceholder")} autoFocus /></Field>
          <Field label={t("admin:common.nameAr")}><Input value={nameAr} dir="rtl" onChange={(e) => setNameAr(e.target.value)} /></Field>
          <Field label={t("admin:common.key")} hint={t("admin:automations.create.keyHint")} error={keyError ?? undefined} className="sm:col-span-2"><Input value={effectiveKey} dir="ltr" className="font-mono" onChange={(e) => { setKeyTouched(true); setKey(e.target.value.toLowerCase()); }} /></Field>
          <Field label={t("admin:automations.trigger.kind")} className="sm:col-span-2">
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setStart("event")} className={choice(start === "event")}><BellRing className="mb-1 size-4 text-aegold-700" /><div className="text-sm font-medium">{t("admin:automations.trigger.modes.event")}</div></button>
              <button type="button" onClick={() => setStart("schedule")} className={choice(start === "schedule")}><CalendarClock className="mb-1 size-4 text-aegold-700" /><div className="text-sm font-medium">{t("admin:automations.trigger.modes.schedule")}</div></button>
            </div>
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("admin:common.cancel")}</Button>
          <Button variant="gold" disabled={!ready} loading={actions.create.isPending} onClick={create}><Plus /> {t("admin:automations.create.createOpen")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
